import { Routes, Route, useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, Suspense, lazy } from 'react';
import { Toaster } from 'sileo';
import 'sileo/styles.css';
import Header from './components/Header';
import Footer from './components/Footer';
import BarraNavInferior from './components/BarraNavInferior';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';
import { useSocialLinks } from './hooks/useSocialLinks';

/**
 * Construye el enlace de WhatsApp a partir del valor configurado en
 * Admin -> Configuracion -> Redes sociales.
 * Acepta un enlace completo ("https://wa.me/57...") que se usa tal cual,
 * o solo el numero ("+57 300 123 4567"), que se normaliza a
 * https://wa.me/<solo digitos>.
 * Devuelve null cuando no hay un valor utilizable (no se inventa un numero).
 */
function construirEnlaceWhatsApp(valor) {
  const texto = String(valor || '').trim();
  if (!texto) return null;
  if (/^https?:\/\//i.test(texto)) return texto;
  const digitos = texto.replace(/\D/g, '');
  return digitos ? `https://wa.me/${digitos}` : null;
}

const OfertaPage = lazy(() => import('./pages/OfertaPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const MyAccountPage = lazy(() => import('./pages/MyAccountPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const OfertasListPage = lazy(() => import('./pages/OfertasListPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const ActivarPage = lazy(() => import('./pages/ActivarPage'));

function BoldReturnHandler({ children }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const boldOrderId = searchParams.get('bold-order-id');
  const socialToken = searchParams.get('social_token');

  useEffect(() => {
    if (boldOrderId) {
      const txStatus = searchParams.get('bold-tx-status') || 'pending';
      navigate(`/checkout/gracias?bold-order-id=${boldOrderId}&bold-tx-status=${txStatus}`, { replace: true });
    }
  }, [boldOrderId, searchParams, navigate]);

  useEffect(() => {
    if (socialToken) {
      localStorage.setItem('auth_token', socialToken);
      try {
        const userStr = searchParams.get('social_user');
        if (userStr) localStorage.setItem('auth_user', decodeURIComponent(userStr));
      } catch {}
      navigate('/', { replace: true });
      window.location.reload();
    }
  }, [socialToken, searchParams, navigate]);

  if (boldOrderId || socialToken) return null;
  return children;
}

function Lazy({ children }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

export default function App() {
  const socialLinks = useSocialLinks();
  const enlaceWhatsApp = construirEnlaceWhatsApp(socialLinks.whatsapp);

  return (
    <>
      <Toaster position="top-center" />
      <Header />
      <main className="main-publico">
        <Routes>
          <Route path="/" element={<BoldReturnHandler><HomePage /></BoldReturnHandler>} />
          <Route path="/ofertas" element={<Lazy><OfertasListPage /></Lazy>} />
          <Route path="/categoria/:slug" element={<Lazy><OfertasListPage isCategory /></Lazy>} />
          <Route path="/ofertas/:slug" element={<Lazy><OfertaPage /></Lazy>} />
          <Route path="/checkout/gracias" element={<Lazy><ThankYouPage /></Lazy>} />
          <Route path="/mi-cuenta" element={<Lazy><MyAccountPage /></Lazy>} />
          <Route path="/activar" element={<Lazy><ActivarPage /></Lazy>} />
          <Route path="/ayuda" element={<Lazy><HelpCenterPage /></Lazy>} />
          <Route path="/terminos" element={<Lazy><LegalPage section="terminos" /></Lazy>} />
          <Route path="/privacidad" element={<Lazy><LegalPage section="privacidad" /></Lazy>} />
          <Route path="/cookies" element={<Lazy><LegalPage section="cookies" /></Lazy>} />
          <Route path="/reembolsos" element={<Lazy><LegalPage section="reembolsos" /></Lazy>} />
          <Route path="/recuperar" element={<Lazy><PasswordResetPage /></Lazy>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <BarraNavInferior />
      {/* El boton flotante solo aparece si hay un WhatsApp configurado desde el panel. */}
      {enlaceWhatsApp && (
      <a
        href={enlaceWhatsApp}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Contactar por WhatsApp"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
      )}
    </>
  );
}
